type DateInput = Date | string | number;
type TimeZoneNameStyle = NonNullable<Intl.DateTimeFormatOptions['timeZoneName']>;

interface TimeZoneNameOptions {
  timeZone?: string;
  locale?: string;
}

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** The zone's name on that date: "EST" in January and "EDT" in July for America/New_York. */
export function getTimeZoneName(
  date: DateInput,
  {
    timeZone = browserTimeZone(),
    locale = 'en-US',
    style = 'short',
  }: TimeZoneNameOptions & { style?: TimeZoneNameStyle } = {},
): string {
  const part = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: style })
    .formatToParts(new Date(date))
    .find(({ type }) => type === 'timeZoneName');
  return (part?.value ?? timeZone).replace(/^GMT(?=[+-]|$)/, 'UTC');
}

export function describeTimeZone(date: DateInput, options: TimeZoneNameOptions = {}): string {
  const timeZone = options.timeZone ?? browserTimeZone();
  const generic = getTimeZoneName(date, { ...options, timeZone, style: 'longGeneric' });
  // A zone with no generic name, such as UTC, gets an offset back instead.
  const name = /^UTC(?=[+-]|$)/.test(generic)
    ? getTimeZoneName(date, { ...options, timeZone, style: 'long' })
    : generic;
  const offset = getTimeZoneName(date, { ...options, timeZone, style: 'longOffset' });
  return `${name} (${timeZone}), ${offset}`;
}
